'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import EmailAddressPattern from 'PatternValidationJS/patterns/EmailAddress';
import GraderActionCreator from '../actions/GraderActionCreator';
import HomepageLabelledInput from '../components/HomepageLabelledInput';
import UrlPattern from 'PatternValidationJS/patterns/Url';
import InputUtil from '../util/InputUtil';
import { navigateTo } from '../util/History';
import { TESTS } from '../constants/Paths';
import { getEmail } from '../selectors/Contact';
import { getSearchParams } from '../util/Urls';
import useQuery from '../hooks/useQuery';
const PRIVACY_POLICY_URL = 'https://legal.hubspot.com/privacy-policy';

const getLangPolicyUrl = () => {
  const lang = window.I18N_MANUAL_LANG;

  if (lang !== 'en') {
    switch (lang) {
      case 'pt':
        return `https://legal.hubspot.com/br/privacy-policy`;

      case 'ja':
        return `https://legal.hubspot.com/jp/privacy-policy`;

      default:
        return `https://legal.hubspot.com/${lang}/privacy-policy`;
    }
  }

  return PRIVACY_POLICY_URL;
};

const HomepageForm = () => {
  const {
    website = ''
  } = useQuery();
  const dispatch = useDispatch();
  const [websiteUrl, setWebsiteUrl] = useState(website);
  const [ignoreValidity, setIgnoreValidity] = useState(true);
  const contactEmail = useSelector(getEmail);
  const [emailAddress, setEmailAddress] = useState(contactEmail);

  const isWebsiteValid = () => {
    return UrlPattern.test(websiteUrl);
  };

  const isEmailValid = () => {
    return EmailAddressPattern.test(emailAddress);
  };

  const isInputValid = () => {
    return isEmailValid() && isWebsiteValid();
  };

  const formSubmitted = () => {
    dispatch(GraderActionCreator.storeContact(emailAddress));
  };

  const getButtonKey = () => {
    return 'home.form.button';
  };

  const buttonClicked = event => {
    // Don't ignore validity anymore
    setIgnoreValidity(false);

    if (isInputValid()) {
      const parsedWebsiteUrl = InputUtil.parseWebsiteUrl(websiteUrl);
      const search = getSearchParams(); // Go to the results page

      navigateTo(`${TESTS}/${parsedWebsiteUrl}${search}`); // Form has been submitted and we've moving to the next page

      formSubmitted();
    }

    event.preventDefault();
    return false;
  };

  const handleSubmit = event => {
    event.preventDefault();
    return false;
  };

  return /*#__PURE__*/_jsxs("form", {
    className: "website-input",
    "data-test-id": "homepage-form",
    onSubmit: handleSubmit,
    children: [/*#__PURE__*/_jsx(HomepageLabelledInput, {
      type: "url",
      "data-test-id": "url",
      errorText: I18n.text('home.form.website.errorText'),
      labelText: I18n.text('home.form.website.text'),
      onChange: event => {
        const {
          value
        } = event.target;
        setWebsiteUrl(value);
      },
      onBlur: event => {
        const {
          value
        } = event.target;
        setWebsiteUrl(value.trim());
      },
      value: websiteUrl,
      isValid: () => ignoreValidity || isWebsiteValid()
    }), /*#__PURE__*/_jsx(HomepageLabelledInput, {
      type: "email",
      "data-test-id": "email",
      errorText: I18n.text('home.form.email.errorText'),
      noticeText: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "home.form.email.noticeText",
        options: {
          target: getLangPolicyUrl()
        }
      }),
      labelText: I18n.text('home.form.email.text'),
      onChange: event => {
        const {
          value
        } = event.target;
        setEmailAddress(value);
      },
      onBlur: event => {
        const {
          value
        } = event.target;
        setEmailAddress(value.trim());
      },
      value: emailAddress,
      isValid: () => ignoreValidity || isEmailValid()
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx("button", {
      "data-test-id": "homepage-button-submit",
      className: "button primary",
      type: "submit",
      onClick: buttonClicked,
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: getButtonKey()
      })
    })]
  });
};

export default HomepageForm;